import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import {
  useSpring, animated, easings,
} from 'react-spring';
import { useDropzone } from 'react-dropzone';
import ResizeObserver from 'react-resize-observer';

import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';
import { saveWorkoutTemplate, fetchWorkoutTemplateData } from '../ducks/calendarActions';
import { showErrorToast } from '../../../shared/utils/toastManager';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

const ModalTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const HelperText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: #444444;
  color: white;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
  margin-bottom: 8px;
`;

const SecondaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: white;
  color: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid #444444;
`;

// First Slide Components

const DateLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  margin-bottom: 8px;
  display: block;
`;

const DayCountText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  text-align: right;
  margin-top: 16px;
`;

// Second Slide Components
const FormGroup = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

const Label = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
  display: block;
  margin-top: 10px;
  margin-bottom: 0px;
`;

const Input = styled('input')`
  width: 100%;
  height: 40px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #444444;
  font-family: 'Nunito Sans';

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const TextArea = styled('textarea')`
  width: 100%;
  height: 100px;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  color: #444444;
  font-family: 'Nunito Sans';
  resize: none;

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const CheckboxContainer = styled('div')`
  display: flex;
  gap: 24px;
  margin-top: 16px;
`;

const CheckboxGroup = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled('input')`
  cursor: pointer;
`;

const CheckboxLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444444;
  cursor: pointer;
`;

const ImageUploadArea = styled('div')`
  width: 100%;
  height: 120px;
  border: 1px dashed ${(props) => (props.isDragActive ? '#444444' : '#EEEEEE')};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.isDragActive ? 'rgba(68, 68, 68, 0.05)' : 'white')};
  position: relative;
  overflow: hidden;
  margin-top: 8px;

  &:hover {
    border-color: #444444;
    background: rgba(68, 68, 68, 0.05);
  }
`;

const ImagePreviewContainer = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F5F5F5;
`;

const ImagePreview = styled('img')`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const RemoveButton = styled('div')`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ImageUploadText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #9E9E9E;
  margin-top: 8px;
`;

const DatePickerRow = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const FileTypeText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9E9E9E;
`;

const TextAndPicker = styled('div')`
  display: flex;
  flex-direction: column;
  width: 48%;
`;

const SaveWorkoutModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const workoutTemplateData = useSelector((state) => state.calendar.data.workoutTemplateData);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);

  // Use modalData.date if provided (from week view), otherwise use selectedDate from Redux
  const initialDate = modalData?.date ? new Date(modalData.date) : new Date(selectedDate);

  // State for the form data
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);
  const [calculatedActiveDays, setCalculatedActiveDays] = useState(null);
  const [fetchedDateRange, setFetchedDateRange] = useState(null); // { start, end }

  // String versions for TBDatePicker
  const [startDateString, setStartDateString] = useState(moment(initialDate).format('YYYY-MM-DD'));
  const [endDateString, setEndDateString] = useState(moment(initialDate).format('YYYY-MM-DD'));

  // Date picker handlers that update both formats
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));
  };

  const handleEndDateChange = (date) => {
    const momentDate = moment(date);
    setEndDate(momentDate.toDate());
    setEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saveWithTitles, setSaveWithTitles] = useState(true);
  const [saveWithPrescribedLoads, setSaveWithPrescribedLoads] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for slide management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(true);

  // Calculate total days between start and end date (inclusive)
  const totalDaysCount = startDate && endDate
    ? moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days') + 1 : 0;

  // Use calculated active days if available, otherwise fall back to total days
  const activeDaysCount = calculatedActiveDays !== null ? calculatedActiveDays : totalDaysCount;

  // Handle image upload
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setUploadError('Please upload a valid image file');
      return;
    }

    const file = acceptedFiles[0];
    setUploadError(null);
    setHeaderImage({
      file,
      preview: URL.createObjectURL(file),
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setHeaderImage(null);
    setUploadError(null);
  };

  // Calculate active days from workout template data
  const calculateActiveDays = (templateData) => {
    let activeDays = 0;
    Object.keys(templateData).forEach((date) => {
      const dateData = templateData[date];
      // Check if the date has exercises (but not just recurring workouts)
      if (dateData?.exercises && dateData.exercises.length > 0) {
        activeDays += 1;
      }
    });
    return activeDays;
  };

  // Check if the current date range needs new data
  const needsNewData = (newStart, newEnd, currentRange) => {
    if (!currentRange) return true;

    const newStartMoment = moment(newStart);
    const newEndMoment = moment(newEnd);
    const currentStartMoment = moment(currentRange.start);
    const currentEndMoment = moment(currentRange.end);

    // Return true if new dates are outside the current range
    return newStartMoment.isBefore(currentStartMoment) || newEndMoment.isAfter(currentEndMoment);
  };

  // Fetch workout data and calculate active days
  const fetchAndCalculateData = async (start, end) => {
    try {
      const templateData = await dispatch(
        fetchWorkoutTemplateData(currentUser, start, end),
      );

      // Calculate active days from the fetched data
      const activeDays = calculateActiveDays(templateData);
      setCalculatedActiveDays(activeDays);

      // Update the fetched range
      setFetchedDateRange({ start, end });
    } catch (error) {
      // Silently handle error - user will see no data update
      // Could add more sophisticated error handling here if needed
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    if (currentUser && startDate && endDate) {
      fetchAndCalculateData(startDate, endDate);
    }
  }, [currentUser]); // Only run once when component mounts with currentUser

  // Watch for date changes and fetch data if needed
  useEffect(() => {
    if (currentUser && startDate && endDate) {
      if (needsNewData(startDate, endDate, fetchedDateRange)) {
        fetchAndCalculateData(startDate, endDate);
      } else if (fetchedDateRange && workoutTemplateData) {
        // If we already have the data, just recalculate active days for the current range
        const activeDays = calculateActiveDays(workoutTemplateData);
        setCalculatedActiveDays(activeDays);
      }
    }
  }, [startDate, endDate, workoutTemplateData]);

  // Alternative display method that doesn't use animation for slide 2
  const showSlide1 = currentSlide === 0;
  const showSlide2 = currentSlide === 1;

  // Animation for slide transitions
  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
  });

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: showSlide2 ? 'translateX(0%)' : 'translateX(100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      // When animation completes, allow overflow for datepicker popovers
      setHideOverflow(false);
    },
  });

  // Handle next slide
  const handleNext = () => {
    // Validate first slide data
    if (!startDate || !endDate) {
      showErrorToast('Please select both start and end dates');
      return;
    }

    // Hide overflow during animation
    setHideOverflow(true);
    setCurrentSlide(1);
  };

  // Handle back
  const handleBack = () => {
    // Hide overflow during animation
    setHideOverflow(true);
    setCurrentSlide(0);
  };

  // Handle final save
  const handleSave = () => {
    // Validate required fields
    if (!title.trim()) {
      showErrorToast('Title is required');
      return;
    }
    if (!startDate) {
      showErrorToast('Start date is required');
      return;
    }
    if (!endDate) {
      showErrorToast('End date is required');
      return;
    }

    setIsSubmitting(true);

    // Prepare the template object to match the required API structure
    const template = {
      startDate,
      endDate,
      title,
      description: description || 'description',
      saveWithTitles, // Will be mapped to includeWorkoutInfo in the action
      saveWithPrescribedLoads, // Will be mapped to includePrescribedLoads in the action
      headerImage: headerImage?.file,
    };

    dispatch(saveWorkoutTemplate(currentUser.accountCode, template))
      .then(() => {
        closeModal();
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <ModalContainer hideOverflow={hideOverflow}>

      <ResizeObserver
        onResize={() => {
          setIsResizing(true);
          // Reset resizing state after animation would have completed
          setTimeout(() => {
            setIsResizing(false);
          }, 300);
        }}
      />

      <ModalTitle>Save to Program Library</ModalTitle>
      <HelperText>
        Saving to the Program Library allows you to re-use a set of built out workouts that can be
        loaded onto another calendar or assigned to your athletes outside of the calendar.
      </HelperText>

      {showSlide1 && (
        <animated.div style={slide1Animation}>
          <div style={{ width: '100%' }}>
            <DatePickerRow>
              <TextAndPicker>
                <DateLabel>Start Date</DateLabel>
                <TBDatePicker
                  activeDate={startDateString}
                  setActiveDate={handleStartDateChange}
                  additionalStyles={{
                    margin: '8px 0 0 0',
                    maxWidth: '100%',
                  }}
                  showDropShadow={false}
                  showIcon={false}
                  maxDate={null}
                />
              </TextAndPicker>

              <TextAndPicker>
                <DateLabel>End Date</DateLabel>
                <TBDatePicker
                  activeDate={endDateString}
                  setActiveDate={handleEndDateChange}
                  additionalStyles={{
                    margin: '8px 0 0 0',
                    maxWidth: '100%',
                  }}
                  showDropShadow={false}
                  showIcon={false}
                  maxDate={null}
                />
              </TextAndPicker>
            </DatePickerRow>

            <DayCountText>
              Active Days:
              {' '}
              {activeDaysCount}
              {' '}
              &nbsp; Total Days:
              {' '}
              {totalDaysCount}
            </DayCountText>

            <ButtonsContainer>
              <PrimaryButton onClick={handleNext}>NEXT</PrimaryButton>
              <SecondaryButton onClick={closeModal}>CANCEL</SecondaryButton>
            </ButtonsContainer>
          </div>
        </animated.div>
      )}

      {showSlide2 && (
        <animated.div style={slide2Animation}>
          <div style={{ width: '100%' }}>
            <FormGroup>
              <Label>Title</Label>
              <Input
                type='text'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='ex. Hypertrophy - Lower Body'
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder='Enter a description of this program'
              />
            </FormGroup>

            <CheckboxContainer>
              <CheckboxGroup>
                <Checkbox
                  type='checkbox'
                  id='save-titles'
                  checked={saveWithTitles}
                  onChange={() => setSaveWithTitles(!saveWithTitles)}
                />
                <CheckboxLabel htmlFor='save-titles'>Save workout titles and colors</CheckboxLabel>
              </CheckboxGroup>

              <CheckboxGroup>
                <Checkbox
                  type='checkbox'
                  id='save-loads'
                  checked={saveWithPrescribedLoads}
                  onChange={() => setSaveWithPrescribedLoads(!saveWithPrescribedLoads)}
                />
                <CheckboxLabel htmlFor='save-loads'>Save prescribed loads</CheckboxLabel>
              </CheckboxGroup>
            </CheckboxContainer>

            <FormGroup>
              <Label>Header Image</Label>
              <Text style={{ fontSize: '12px', color: '#9E9E9E', marginBottom: '8px' }}>
                Recommended size: 1060 x 420
              </Text>
              <ImageUploadArea {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} />
                {headerImage ? (
                  <ImagePreviewContainer>
                    <ImagePreview src={headerImage.preview} alt='Header preview' />
                    <RemoveButton onClick={handleRemoveImage}>
                      <IcomoonReact iconSet={iconSet} icon='close' size={16} color='#FFFFFF' />
                    </RemoveButton>
                  </ImagePreviewContainer>
                ) : (
                  <>
                    <ImageUploadText>Click or drag image here.</ImageUploadText>
                    <FileTypeText>Accepted file types: jpg, jpeg, png</FileTypeText>
                  </>
                )}
              </ImageUploadArea>
              {uploadError && <Text color='#E53935' fontSize='12px' style={{ marginTop: '8px' }}>{uploadError}</Text>}
              <Text color='#9E9E9E' fontSize='12px' style={{ marginTop: '8px' }}>Note: Recurring workout items cannot currently be saved. Click here to learn more about recurring items.</Text>
            </FormGroup>

            <ButtonsContainer>
              <PrimaryButton onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? 'SAVING...' : 'SAVE'}
              </PrimaryButton>
              <SecondaryButton onClick={handleBack}>BACK</SecondaryButton>
            </ButtonsContainer>
          </div>
        </animated.div>
      )}
    </ModalContainer>
  );
};

SaveWorkoutModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFromWeekView: PropTypes.bool,
  }),
};

SaveWorkoutModal.defaultProps = {
  modalData: null,
};

export default SaveWorkoutModal;
